import { useTranslation } from 'react-i18next';
import { InfoWindow } from '@vis.gl/react-google-maps';
import Button from '../../button/Button';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import { WfWorkplace } from 'src/api/wf/models/WfWorkplace';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import LinkButton from '../../button/LinkButton';
import WF_SitesIcon from '../icon/SitesIcon';
import { useNavigate } from 'react-router-dom';
import { wfNavigate } from 'src/ui/wf/router/WfScreenRegistration';
import { WfScreensMap } from 'src/ui/wf/router/WfScreensMap';
import { WF_Screen } from 'src/ui/wf/router/WfScreens';
import { getOperationStatusTranslationKey } from 'src/api/wf/models/enums/WfOperationtStatus';
import { useWfAlertHistoryStore } from 'src/api/wf/stores/WfAlertHistoryStore';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { useWfLocationService } from 'src/api/wf/service/WfLocationService';
import { useNotifyMap } from 'src/ui/common/alert/useNotifyMap';

type MarkerProps = {
  site: WfWorkplace;
  canOpenPopup: boolean;
  popupOpened?: () => void;
  popupClosed?: () => void;
  isPopupOpen?: boolean;
};

function WF_WorkplaceMarker({ site, canOpenPopup, popupClosed, isPopupOpen }: MarkerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userAgent = useUserAgent();
  const { patchHistorySearchParams } = useWfAlertHistoryStore();
  const { setSelectedWorkplace } = useWfWorkplaceStore();
  const { user } = useWfUserStore();
  const { createWorkplaceReservation } = useWfWorkplaceService();
  const { updateWfUserLocation } = useWfLocationService();
  const notifyMap = useNotifyMap();

  const handleOpenWorkplaceDetails = () => {
    setSelectedWorkplace(site);
    wfNavigate(navigate, WfScreensMap[WF_Screen.WorkplaceDetails].path);
  };

  const handleOpenAlertHistory = () => {
    patchHistorySearchParams({
      buildingName: site.name,
      branchCode: site.branchCode,
      salesOfficeCode: site.salesOfficeCode,
      organizationCode: site.organizationCode,
      customerNumber: site.customerId.toString(),
    });
    wfNavigate(navigate, WfScreensMap[WF_Screen.AlertHistory].path);
  };

  const handleSelection = () => {
    setSelectedWorkplace(site);
  };

  const handleReservation = async () => {
    popupClosed?.();
    if (!site || !user) return;

    const reservationParams = {
      engineerUserId: user.id,
      customerId: site.customerId,
      branchCode: site.branchCode,
    };

    try {
      const response = await createWorkplaceReservation(reservationParams);

      if (response) {
        notifyMap('wf.searchHeader.reservationSuccess');
        if (userAgent.isMobile) {
          updateWfUserLocation();
        }
      } else {
        notifyMap('wf.searchHeader.reservationFailed');
      }
    } catch (error) {
      console.error('Error with canceling or fetching updated data:', error);
      notifyMap('wf.searchHeader.reservationFailed');
    }
  };

  const siteReserved = site.details.reservations.length !== 0;
  const icon = WF_SitesIcon(siteReserved, site.details.statusId, site.sectionCode);

  return (
    <>
      <div className="cursor-pointer">{icon}</div>

      {isPopupOpen && canOpenPopup && site.location && (
        <InfoWindow
          position={{ lat: site.location.latitude, lng: site.location.longitude }}
          onCloseClick={() => {
            popupClosed?.();
          }}
        >
          <div className="flex flex-col gap-2 p-1 text-sm text-black">
            <LinkButton title={site.customerId.toString()} onClick={handleOpenWorkplaceDetails} />
            <label className="m-0 text-center font-medium">{`${site.emergencyClass ? '★ ' : ''}${site.name}`}</label>
            <label className="m-0 text-center font-bold">
              {`${t(getOperationStatusTranslationKey(site.details.statusId))}`}
            </label>

            <Button
              className="rounded border border-blue-600 px-3 py-1 text-blue-600 transition hover:bg-blue-600 hover:text-white"
              title={t('wf.markers.history')}
              onClick={handleOpenAlertHistory}
            />

            {userAgent.isMobile && !siteReserved && (
              <Button
                title={t('wf.markers.reserve')}
                onClick={handleReservation}
                disabled={siteReserved}
              />
            )}

            {userAgent.isDesktop && (
              <Button title={t('wf.markers.select')} onClick={handleSelection} />
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
}
export default WF_WorkplaceMarker;
